import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@notifications/services/notifications.service';
import { PlatformService } from '@panamax/app-state';
import { OrderItem } from '@usf/ngrx-order';
import { ProductSummary } from '@usf/product-types/Product-Summary';
import { BehaviorSubject, Observable, filter, map, take, tap } from 'rxjs';
import { ImageVariantEnum } from 'src/app/product-detail/models/image-variant.enum';
import {
  getAlternativeThumbnail,
  getFirstImageURL,
} from 'src/app/product-detail/utils/product-images.util';
import { TagProduct } from 'src/app/shared/components/usf-product-tag-list/models/tag-product.model';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';

interface ProductSubstitutionCardViewModel {
  lastItem: boolean;
  lastUpdatedBy: string;
  item: OrderItem;
  subbedItem?: OrderItem;
  subbedProductSummary?: ProductSummary;
  displayTags: TagProduct[];
  processed?: boolean;
  eachesOrdered?: number;
  unitsOrdered?: number;
  productSummary: ProductSummary;
}

export interface SubstitutionDetail {
  unitsOrdered?: number;
  eachesOrdered?: number;
  orderItem: any;
  product: any;
}

export interface ProductSubstitution {
  original: SubstitutionDetail | null;
  substitute: SubstitutionDetail | null;
  isSelected: boolean;
}

@Component({
  selector: 'app-product-substitution-card',
  templateUrl: './product-substitution-card.component.html',
  styleUrls: ['./product-substitution-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSubstitutionCardComponent implements OnInit {
  private _productSubstitutionSubject =
    new BehaviorSubject<ProductSubstitution | null>(null);

  vm$: Observable<ProductSubstitutionCardViewModel>;
  productSummary: ProductSummary;
  @Input()
  get productSubstitution(): ProductSubstitution {
    return this._productSubstitutionSubject.value as ProductSubstitution;
  }

  set productSubstitution(value: ProductSubstitution) {
    this._productSubstitutionSubject.next(value);
  }

  @Output() productSelect: EventEmitter<boolean> = new EventEmitter<boolean>();

  maxDisplayTags = 5;
  thumbnailImageURL: string;
  splittedTrackingNumbers: string[];
  expectedInStock: string;

  constructor(
    readonly platformService: PlatformService,
    private readonly navHelper: NavigationHelperService,
    private platform: Platform,
    private translateService: TranslateService,
    readonly notificationsService: NotificationsService,
  ) {}

  ngOnInit(): void {
    this.vm$ = this._productSubstitutionSubject.pipe(
      filter(substitution => !!substitution),
      tap(substitution => {
        this.productSummary = substitution.original?.product;
        this.updateThumbnailImageURL(substitution);
      }),
      map(substitution => ({
        lastItem: false,
        lastUpdatedBy: '',
        item: substitution.original?.orderItem,
        subbedItem: substitution.substitute?.orderItem,
        subbedProductSummary: substitution.substitute?.product?.summary,
        displayTags: [],
        processed: false,
        eachesOrdered: substitution.original?.eachesOrdered,
        unitsOrdered: substitution.original?.unitsOrdered,
        productSummary: substitution.original?.product?.summary,
      })),
    );

    if (this.platform?.is('mobile') && !this.platform?.is('tablet')) {
      this.maxDisplayTags = 2;
    }
  }

  private updateThumbnailImageURL(substitution: ProductSubstitution): void {
    if (substitution.substitute && substitution.substitute.product) {
      this.thumbnailImageURL = getFirstImageURL(
        substitution.substitute.product.summary,
        ImageVariantEnum.Thumbnail,
      );
    } else {
      this.thumbnailImageURL = getFirstImageURL(
        substitution.original.product.summary,
        ImageVariantEnum.Thumbnail,
      );
    }
  }

  productClickHandler() {
    this.navHelper?.routeToProductDetails(this.productSummary?.productNumber);
  }

  orderClickHandler() {
    this.notificationsService
      ?.getCart()
      ?.pipe(take(1))
      ?.subscribe(cart => {
        if (
          cart?.orderItems?.length > 0 &&
          cart?.orderItems?.find(
            item => item?.productNumber === this.productSummary?.productNumber,
          )
        ) {
          this.navHelper?.routeTo('order');
        } else {
          this.navHelper?.routeTo('my-orders-view');
        }
      });
  }

  getAternativeThumbnail = (thumbnailImageURL: string): string =>
    getAlternativeThumbnail(this.productSummary, thumbnailImageURL);
}
